import {
  createContext,
  useContext,
  useSyncExternalStore,
  useCallback,
  type ReactNode,
} from "react";
import type { Organization } from "../types/user";
import { useAuth } from "../hooks/useAuth";

interface OrgContextType {
  activeOrg: Organization | null;
  setActiveOrg: (org: Organization | null) => void;
  switchOrg: (orgId: string) => void;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

const ACTIVE_ORG_KEY = "activeOrgId";

let listeners: Array<() => void> = [];
let activeOrgCache: Organization | null = null;

function getActiveOrgId(): string | null {
  return localStorage.getItem(ACTIVE_ORG_KEY);
}

function setActiveOrgId(orgId: string | null) {
  if (orgId) {
    localStorage.setItem(ACTIVE_ORG_KEY, orgId);
  } else {
    localStorage.removeItem(ACTIVE_ORG_KEY);
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function getSnapshot() {
  return getActiveOrgId();
}

interface OrgProviderProps {
  children: ReactNode;
}

export function OrgProvider({ children }: OrgProviderProps) {
  const { user } = useAuth();
  const savedOrgId = useSyncExternalStore(subscribe, getSnapshot);

  const resolveActiveOrg = useCallback((): Organization | null => {
    if (!user?.organizations || user.organizations.length === 0) {
      return null;
    }

    if (savedOrgId) {
      const savedOrg = user.organizations.find((org) => org.id === savedOrgId);
      if (savedOrg) {
        activeOrgCache = savedOrg;
        return savedOrg;
      }
    }

    const firstOrg = user.organizations[0];
    if (firstOrg && firstOrg.id !== savedOrgId) {
      setActiveOrgId(firstOrg.id);
      activeOrgCache = firstOrg;
      return firstOrg;
    }

    return activeOrgCache;
  }, [user?.organizations, savedOrgId]);

  const activeOrg = resolveActiveOrg();

  const setActiveOrg = (org: Organization | null) => {
    activeOrgCache = org;
    setActiveOrgId(org?.id ?? null);
  };

  const switchOrg = (orgId: string) => {
    if (user?.organizations) {
      const org = user.organizations.find((o) => o.id === orgId);
      if (org) {
        setActiveOrg(org);
      }
    }
  };

  return (
    <OrgContext.Provider value={{ activeOrg, setActiveOrg, switchOrg }}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  const context = useContext(OrgContext);
  if (context === undefined) {
    throw new Error("useOrg must be used within an OrgProvider");
  }
  return context;
}
